// ONE MEETING, OPENED. Clicking a meeting anywhere on the calendar lands here.
//
// It is one dialog rather than a detail view with a separate summary dialog behind it,
// because there is only one thing you usually come here to do: write down what happened.
// The facts sit at the top, the summary form is the body, and everything else — edit,
// hand over, cancel, delete — is a row of quieter actions beneath.
//
// AFTER SAVING, THE BODY BECOMES THE FOLLOW-UP CHOICE rather than opening another dialog
// on top. The question "what follows from this meeting" belongs to the same moment as the
// summary, and stacking a second modal over the first loses the thing just written.
//
// REASSIGNMENT IS HERE, NOT IN THE EDIT FORM. It is its own act with its own endpoint: it
// writes one column and it is the only change that has to ask a DIFFERENT person's diary,
// so it is refused if that partner is already busy then. Putting it among the booking
// fields would make it look like just another edit.
import { useState } from 'react';
import he from '../../i18n/he';
import DateText from '../../components/DateText';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { Select } from '../../components/Form';
import { ErrorText } from '../../components/Loader';
import { meetingsApi } from '../../api/calendar';
import { meetingLabel } from './MeetingChip';
import { isCancelled, isExternal, isHeld, isInternal } from './meetingKind';
import { dotStyle } from './meetingColors';
import SummaryForm from './SummaryForm';
import FollowUpChoice from './FollowUpChoice';

const Fact = ({ label, children }) => (
  <div className="meeting-fact">
    <span className="muted">{label}</span>
    <span>{children}</span>
  </div>
);

export default function MeetingDetail({
  meeting, partners, onClose, onChanged,
  onEdit, onCancelRequest, onDeleteRequest, onFollowUpMeeting, onAddTask, onAddLead,
  onMakeLead, onLinkClient,
}) {
  const [phase, setPhase] = useState('summary'); // 'summary' | 'followUp'
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const saveSummary = async (body) => {
    await meetingsApi.setSummary(meeting.id, body);
    await onChanged();
    setPhase('followUp');
  };

  const reassign = async (partnerId) => {
    setBusy(true);
    setError(null);
    try {
      await meetingsApi.setOwner(meeting.id, Number(partnerId));
      await onChanged();
    } catch (e) {
      setError(e);
    }
    setBusy(false);
  };

  const reinstate = async () => {
    setBusy(true);
    setError(null);
    try {
      await meetingsApi.setStatus(meeting.id, 'scheduled');
      await onChanged();
    } catch (e) {
      setError(e);
    }
    setBusy(false);
  };

  // The owner picker offers everyone; choosing the current owner is a no-op the server
  // short-circuits. An internal meeting cannot be handed to its own other side, so that
  // partner is dropped rather than offered and refused.
  const ownerOptions = partners
    .filter((p) => String(p.id) !== String(meeting.with_partner_id))
    .map((p) => ({ value: p.id, label: p.name }));

  return (
    <Modal title={meetingLabel(meeting)} onClose={onClose}>
      <div className="meeting-facts">
        <Fact label={he.calendar.colWhen}><DateText value={meeting.meeting_date} /></Fact>
        <Fact label={he.calendar.colTime}>{meeting.start_time}–{meeting.end_time}</Fact>
        {isInternal(meeting) && (
          <Fact label={he.calendar.withPartner}>{meeting.owner_name} · {meeting.with_name}</Fact>
        )}
        {/* Someone outside the system (077): the name as typed, and who they are. */}
        {isExternal(meeting) && (
          <Fact label={he.calendar.externalName}>
            {meeting.external_name}
            {meeting.external_details && <span className="muted"> · {meeting.external_details}</span>}
            {' '}<span className="badge gray">{he.calendar.externalBadge}</span>
          </Fact>
        )}
        {!isInternal(meeting) && !isExternal(meeting) && (
          <>
            <Fact label={he.calendar.client}>{meeting.client_name}</Fact>
            {/* Linked from an external meeting: who was actually met. */}
            {meeting.external_name && <Fact label={he.calendar.externalName}>{meeting.external_name}</Fact>}
            {meeting.job_title && <Fact label={he.calendar.job}>{meeting.job_title}</Fact>}
          </>
        )}
        <Fact label={he.calendar.owner}>
          <span className="meeting-dot" style={dotStyle(meeting, partners)} />
          {meeting.owner_name}
        </Fact>
        {isCancelled(meeting) && (
          <Fact label={he.calendar.colStatus}>
            <span className="badge gray">{he.calendar.cancelled}</span>
            {meeting.cancel_reason && <span className="muted"> · {meeting.cancel_reason}</span>}
          </Fact>
        )}
      </div>

      {phase === 'followUp' ? (
        <FollowUpChoice
          meeting={meeting}
          onFollowUpMeeting={onFollowUpMeeting}
          onAddTask={onAddTask}
          onAddLead={onAddLead}
          onMakeLead={onMakeLead}
          onClose={onClose}
        />
      ) : (
        <>
          {/* A cancelled meeting has nothing to summarise — it did not happen — so the
              form is replaced by the one action that makes sense on it. The server refuses
              a summary on a cancelled meeting too. */}
          {isCancelled(meeting) ? (
            <div className="actions">
              <Button onClick={reinstate} disabled={busy}>{he.calendar.reinstate}</Button>
            </div>
          ) : (
            <>
              <h3 className="section-title">
                {isHeld(meeting) ? he.calendar.editSummary : he.calendar.summaryTitle}
              </h3>
              <SummaryForm meeting={meeting} onSave={saveSummary} />
            </>
          )}

          <div className="meeting-actions">
            {/* The person became a lead or turns out to be a client (077): link this meeting
                and its follow-ups to them, so the summaries land on their card. */}
            {isExternal(meeting) && (
              <>
                <Button small variant="subtle" onClick={() => onMakeLead(meeting)}>{he.calendar.makeLead}</Button>
                <Button small variant="subtle" onClick={() => onLinkClient(meeting)}>{he.calendar.linkClient}</Button>
              </>
            )}
            <Button small variant="subtle" onClick={() => onEdit(meeting)}>{he.common.edit}</Button>
            {!isCancelled(meeting) && (
              <Button small variant="subtle" onClick={() => onCancelRequest(meeting)}>
                {he.calendar.cancelMeeting}
              </Button>
            )}
            <Button small variant="subtle" onClick={() => onDeleteRequest(meeting)}>
              {he.common.delete}
            </Button>
          </div>

          {!isCancelled(meeting) && (
            <Select
              label={he.calendar.reassign}
              value={meeting.owner_partner_id}
              onChange={(e) => reassign(e.target.value)}
              options={ownerOptions}
              disabled={busy}
            />
          )}

          {error && <ErrorText error={error} />}
        </>
      )}
    </Modal>
  );
}
